import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import AdminNavbar from '../components/AdminNavbar';
import { Shield, Save, RefreshCw, Plus, Trash2 } from 'lucide-react';

const AdminSecurityPage = () => {
    const [settings, setSettings] = useState({
        require2FA: false,
        maxLoginAttempts: 5,
        sessionTimeout: 24,
        ipWhitelist: [],
        ipBlacklist: []
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [newWhitelistIP, setNewWhitelistIP] = useState('');
    const [newBlacklistIP, setNewBlacklistIP] = useState('');

    useEffect(() => { fetchSettings(); }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const res = await api.get('/admin/settings/security');
            if (res.data.securitySettings) setSettings(res.data.securitySettings);
        } catch (error) {
            toast.error('Failed to load security settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await api.put('/admin/settings/security', { securitySettings: settings });
            toast.success('Security settings saved');
        } catch (error) {
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const addToList = (list, value, setter) => {
        if (!value.trim()) return;
        if (settings[list].includes(value)) return toast.error('IP already in list');
        setSettings({ ...settings, [list]: [...settings[list], value.trim()] });
        setter('');
    };

    const removeFromList = (list, ip) => {
        setSettings({ ...settings, [list]: settings[list].filter(i => i !== ip) });
    };

    if (loading) return <div className="min-h-screen bg-slate-900"><AdminNavbar /><div className="text-center py-12 text-gray-400">Loading...</div></div>;

    return (
        <div className="min-h-screen bg-slate-900">
            <AdminNavbar />
            <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Shield className="text-red-400" /> Security Settings</h1>
                        <p className="text-gray-400 text-sm">Configure platform security</p>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={fetchSettings} className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"><RefreshCw size={18} /></button>
                        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 disabled:opacity-50">
                            <Save size={18} /> {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="p-6 max-w-4xl">
                <div className="grid gap-6">
                    {/* General Security */}
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                        <h2 className="text-lg font-bold text-white mb-4">General Security</h2>
                        <div className="space-y-4">
                            <label className="flex items-center justify-between">
                                <span className="text-gray-300">Require 2FA for all users</span>
                                <input type="checkbox" checked={settings.require2FA} onChange={e => setSettings({ ...settings, require2FA: e.target.checked })} className="w-5 h-5 rounded" />
                            </label>
                            <div>
                                <label className="text-gray-400 text-sm">Max Login Attempts</label>
                                <input type="number" value={settings.maxLoginAttempts} onChange={e => setSettings({ ...settings, maxLoginAttempts: parseInt(e.target.value) })} className="w-full mt-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                            </div>
                            <div>
                                <label className="text-gray-400 text-sm">Session Timeout (hours)</label>
                                <input type="number" value={settings.sessionTimeout} onChange={e => setSettings({ ...settings, sessionTimeout: parseInt(e.target.value) })} className="w-full mt-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                            </div>
                        </div>
                    </div>

                    {/* IP Whitelist */}
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                        <h2 className="text-lg font-bold text-white mb-4">IP Whitelist (Admin Access)</h2>
                        <div className="flex gap-2 mb-4">
                            <input type="text" placeholder="Enter IP address" value={newWhitelistIP} onChange={e => setNewWhitelistIP(e.target.value)} className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                            <button onClick={() => addToList('ipWhitelist', newWhitelistIP, setNewWhitelistIP)} className="px-4 py-2 bg-green-600 text-white rounded-lg"><Plus size={18} /></button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {settings.ipWhitelist.map(ip => (
                                <span key={ip} className="flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                                    {ip} <button onClick={() => removeFromList('ipWhitelist', ip)}><Trash2 size={14} /></button>
                                </span>
                            ))}
                            {settings.ipWhitelist.length === 0 && <span className="text-gray-500">No IPs whitelisted</span>}
                        </div>
                    </div>

                    {/* IP Blacklist */}
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                        <h2 className="text-lg font-bold text-white mb-4">IP Blacklist (Blocked)</h2>
                        <div className="flex gap-2 mb-4">
                            <input type="text" placeholder="Enter IP address" value={newBlacklistIP} onChange={e => setNewBlacklistIP(e.target.value)} className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                            <button onClick={() => addToList('ipBlacklist', newBlacklistIP, setNewBlacklistIP)} className="px-4 py-2 bg-red-600 text-white rounded-lg"><Plus size={18} /></button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {settings.ipBlacklist.map(ip => (
                                <span key={ip} className="flex items-center gap-2 px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm">
                                    {ip} <button onClick={() => removeFromList('ipBlacklist', ip)}><Trash2 size={14} /></button>
                                </span>
                            ))}
                            {settings.ipBlacklist.length === 0 && <span className="text-gray-500">No IPs blacklisted</span>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminSecurityPage;
